import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { getPartners, invalidateBanners } from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';

// Marks a listing as a homepage banner rather than something for sale. It is
// deliberately NOT one of the marketplace's configured listing types, and
// banners are never published — they stay drafts, which Sharetribe keeps out
// of the public listing query entirely. That is what keeps them off the
// search page and off /l/<id>, structurally rather than by a filter someone
// could later switch off. The homepage reads them through our own server,
// which can see drafts via the Integration API (server/api/banners.js).
export const BANNER_LISTING_TYPE = 'banner';

// Where a banner is shown; server/api/banners.js reads the same values. A
// banner with no placement is a homepage banner, which covers every banner
// saved before placements existed.
export const PLACEMENT_HOMEPAGE = 'homepage';
export const PLACEMENT_PARTNER_INTRO = 'partner-intro';
export const PLACEMENT_PARTNER = 'partner';
export const PLACEMENT_PARTNER_INVITE = 'partner-invite';
export const BANNER_PLACEMENTS = [
  PLACEMENT_HOMEPAGE,
  PLACEMENT_PARTNER_INTRO,
  PLACEMENT_PARTNER,
  PLACEMENT_PARTNER_INVITE,
];
// Placements that are a picture first: the homepage banner and the Butikker
// page's top and closing banners.
export const PLACEMENTS_NEEDING_IMAGE = [
  PLACEMENT_HOMEPAGE,
  PLACEMENT_PARTNER_INTRO,
  PLACEMENT_PARTNER_INVITE,
];

// Image variants: a small one for the admin thumbnail, big ones for the
// homepage. All are Sharetribe's built-in scaled variants, so no custom
// variant config is needed.
const IMAGE_VARIANTS = ['scaled-small', 'scaled-large', 'scaled-xlarge'];
const IMAGE_FIELDS = IMAGE_VARIANTS.map(v => `variants.${v}`);

// Reads.
const QUERY_PARAMS = {
  include: ['images'],
  'fields.image': IMAGE_FIELDS,
  perPage: 100,
};

// Writes. `expand: true` is the important one: without it Sharetribe answers a
// create or update with a bare `{ id, type }` reference and no attributes, and
// storing that shape is what put an unrenderable banner in the list. `perPage`
// has no meaning on a write, so it is not carried over.
const WRITE_PARAMS = {
  expand: true,
  include: ['images'],
  'fields.image': IMAGE_FIELDS,
};

/**
 * Banner listings in the order they should appear, drafts only.
 *
 * `bannerOrder` is a number we control; entries without one (or sharing one)
 * fall back to creation order so the list can never come out arbitrary.
 */
const sortBanners = listings => {
  return [...listings].sort((a, b) => {
    const orderA = a.attributes?.publicData?.bannerOrder;
    const orderB = b.attributes?.publicData?.bannerOrder;
    const safeA = typeof orderA === 'number' ? orderA : Number.MAX_SAFE_INTEGER;
    const safeB = typeof orderB === 'number' ? orderB : Number.MAX_SAFE_INTEGER;
    if (safeA !== safeB) {
      return safeA - safeB;
    }
    return new Date(a.attributes?.createdAt) - new Date(b.attributes?.createdAt);
  });
};

// ================ Async thunks ================ //

const fetchBannersPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  return sdk.ownListings
    .query({ ...QUERY_PARAMS, pub_listingType: BANNER_LISTING_TYPE })
    .then(response => {
      const listings = denormalisedResponseEntities(response);
      // Own listings include every state; a banner only ever exists as a
      // draft, so anything else here is not ours to show. The attributes
      // guard keeps a half-formed entity from ever reaching the list.
      return sortBanners(listings.filter(l => l.attributes?.state === 'draft'));
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchBannersThunk = createAsyncThunk(
  'BannerAdminPage/fetchBanners',
  fetchBannersPayloadCreator
);
export const fetchBanners = () => dispatch => {
  return dispatch(fetchBannersThunk()).unwrap();
};

/**
 * Upload one image and hand back its id, so the caller can attach it to a
 * banner on save. Mirrors the listing wizard's upload step
 * (EditListingPage.duck) — same SDK call, same variants.
 */
export const uploadBannerImageThunk = createAsyncThunk(
  'BannerAdminPage/uploadBannerImage',
  ({ file }, { extra: sdk, rejectWithValue }) => {
    return sdk.images
      .upload({ image: file }, { expand: true, 'fields.image': QUERY_PARAMS['fields.image'] })
      .then(response => {
        const image = response.data.data;
        return { imageId: image.id, image };
      })
      .catch(e => rejectWithValue(storableError(e)));
  }
);
export const uploadBannerImage = file => dispatch => {
  return dispatch(uploadBannerImageThunk({ file })).unwrap();
};

/**
 * Create or update one banner. `id` decides which: a new banner is created as
 * a draft and never published (see BANNER_LISTING_TYPE).
 *
 * A new banner goes to the end of the list, so creating one never reshuffles
 * what is already there.
 */
export const saveBannerThunk = createAsyncThunk(
  'BannerAdminPage/saveBanner',
  (
    {
      id,
      title,
      description,
      link,
      cta,
      imagePosition,
      imageAlt,
      active,
      imageId,
      mobileImageId,
      removeMobileImage,
      currentImageId,
      currentMobileImageId,
      placement,
      partnerId,
    },
    { getState, extra: sdk, rejectWithValue }
  ) => {
    const existing = getState().BannerAdminPage.banners;
    const nextOrder = existing.length;

    const safePlacement = BANNER_PLACEMENTS.includes(placement) ? placement : PLACEMENT_HOMEPAGE;
    const isHomepage = safePlacement === PLACEMENT_HOMEPAGE;
    const publicDataMaybe = {
      listingType: BANNER_LISTING_TYPE,
      // Only a homepage banner is a link; the Partner page's banners lead to
      // the partner's products on the page itself.
      bannerLink: isHomepage ? link : null,
      // `null` for the homepage, so a homepage banner keeps exactly the shape
      // it had before placements existed.
      bannerPlacement: isHomepage ? null : safePlacement,
      bannerPartnerId: safePlacement === PLACEMENT_PARTNER ? partnerId || null : null,
      bannerActive: !!active,
      // All three are optional. `null` rather than '' so clearing a field
      // removes the key from publicData instead of storing an empty string,
      // which keeps the homepage's fallbacks simple.
      bannerCta: cta || null,
      bannerImagePosition: imagePosition === 'right' ? 'right' : 'left',
      bannerImageAlt: imageAlt || null,
    };
    // The optional phone picture is the listing's second image, flagged so a
    // banner saved before the field existed is never misread.
    const desktopId = imageId || currentImageId || null;
    const mobileId = removeMobileImage ? null : mobileImageId || currentMobileImageId || null;
    const imagesChanged = !!imageId || !!mobileImageId || !!removeMobileImage;
    // Only stamp an order on creation; editing must not move the banner.
    const orderMaybe = id ? {} : { bannerOrder: nextOrder };
    const imagesMaybe = imagesChanged ? { images: [desktopId, mobileId].filter(Boolean) } : {};
    const mobileFlagMaybe = imagesChanged ? { bannerMobileImage: !!(desktopId && mobileId) } : {};

    const params = {
      title,
      // Sharetribe stores the subtext as the listing description; empty is a
      // valid value (the field is optional) but must be a string, not null.
      description: description || '',
      publicData: { ...publicDataMaybe, ...orderMaybe, ...mobileFlagMaybe },
      ...imagesMaybe,
    };

    const request = id
      ? sdk.ownListings.update({ id, ...params }, WRITE_PARAMS)
      : sdk.ownListings.createDraft(params, WRITE_PARAMS);

    return request
      .then(response => {
        const saved = denormalisedResponseEntities(response)[0];
        if (!saved?.attributes) {
          // Nothing renderable came back. Refuse it rather than putting a
          // half-entity in the list, which is what crashed the page before.
          throw new Error('Banner save returned no listing attributes');
        }
        return saved;
      })
      .catch(e => rejectWithValue(storableError(e)));
  }
);
export const saveBanner = params => dispatch => {
  return dispatch(saveBannerThunk(params))
    .unwrap()
    .then(saved => {
      // The homepage caches banners server-side and has no other way to learn
      // they changed; without this an edit took up to a minute to show up.
      invalidateBanners();
      return saved;
    });
};

/**
 * Remove a banner for good. `discardDraft` is a real deletion (drafts are the
 * only listings Sharetribe lets you delete outright), which is why banners
 * are kept as drafts rather than closed listings — "slett" means slett.
 */
export const deleteBannerThunk = createAsyncThunk(
  'BannerAdminPage/deleteBanner',
  ({ id }, { extra: sdk, rejectWithValue }) => {
    // Callers hand over whichever they hold: the uuid string from a row, or
    // the UUID object from an entity. The reducer matches on the string.
    const uuid = typeof id === 'string' ? id : id?.uuid;
    return sdk.ownListings
      .discardDraft({ id })
      .then(() => ({ uuid }))
      .catch(e => rejectWithValue(storableError(e)));
  }
);
export const deleteBanner = id => dispatch => {
  return dispatch(deleteBannerThunk({ id }))
    .unwrap()
    .then(result => {
      invalidateBanners();
      return result;
    });
};

/**
 * Persist a new order after a drag. Takes the full list of ids in their new
 * order and writes each one's index; the store is updated optimistically by
 * the reducer so the row stays where it was dropped.
 */
export const reorderBannersThunk = createAsyncThunk(
  'BannerAdminPage/reorderBanners',
  ({ orderedIds }, { extra: sdk, rejectWithValue }) => {
    return Promise.all(
      orderedIds.map((id, index) =>
        sdk.ownListings.update({ id, publicData: { bannerOrder: index } })
      )
    )
      .then(() => ({ orderedIds }))
      .catch(e => rejectWithValue(storableError(e)));
  }
);
export const reorderBanners = orderedIds => dispatch => {
  return dispatch(reorderBannersThunk({ orderedIds }))
    .unwrap()
    .then(result => {
      invalidateBanners();
      return result;
    });
};

/**
 * The verified partners, for the entrance banner's partner picker. Never
 * fails: no partners only means there is nobody to pick yet.
 */
export const fetchPartnersThunk = createAsyncThunk('BannerAdminPage/fetchPartners', () =>
  getPartners().then(response => response.partners || [])
);

// ================ Slice ================ //

const initialState = {
  banners: [],
  fetchInProgress: false,
  fetchError: null,
  saveInProgress: false,
  saveError: null,
  deleteInProgress: false,
  deleteError: null,
  uploadInProgress: false,
  uploadError: null,
  reorderError: null,
  // The verified partners a Partner page entrance can belong to.
  partners: [],
};

const bannerAdminPageSlice = createSlice({
  name: 'BannerAdminPage',
  initialState,
  reducers: {
    // Optimistic reorder: the row must stay where it was dropped, and the
    // save that follows is fire-and-forget.
    bannersReordered: (state, action) => {
      const orderedIds = action.payload;
      const byId = new Map(state.banners.map(b => [b.id.uuid, b]));
      state.banners = orderedIds.map(id => byId.get(id)).filter(Boolean);
      state.reorderError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPartnersThunk.fulfilled, (state, action) => {
        state.partners = action.payload;
      })
      .addCase(fetchBannersThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchBannersThunk.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.banners = action.payload;
      })
      .addCase(fetchBannersThunk.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      })

      .addCase(uploadBannerImageThunk.pending, state => {
        state.uploadInProgress = true;
        state.uploadError = null;
      })
      .addCase(uploadBannerImageThunk.fulfilled, state => {
        state.uploadInProgress = false;
      })
      .addCase(uploadBannerImageThunk.rejected, (state, action) => {
        state.uploadInProgress = false;
        state.uploadError = action.payload;
      })

      .addCase(saveBannerThunk.pending, state => {
        state.saveInProgress = true;
        state.saveError = null;
      })
      .addCase(saveBannerThunk.fulfilled, (state, action) => {
        state.saveInProgress = false;
        const saved = action.payload;
        const index = state.banners.findIndex(b => b.id.uuid === saved.id.uuid);
        if (index >= 0) {
          state.banners[index] = saved;
        } else {
          state.banners.push(saved);
        }
      })
      .addCase(saveBannerThunk.rejected, (state, action) => {
        state.saveInProgress = false;
        state.saveError = action.payload;
      })

      .addCase(deleteBannerThunk.pending, state => {
        state.deleteInProgress = true;
        state.deleteError = null;
      })
      .addCase(deleteBannerThunk.fulfilled, (state, action) => {
        state.deleteInProgress = false;
        state.banners = state.banners.filter(b => b.id.uuid !== action.payload.uuid);
      })
      .addCase(deleteBannerThunk.rejected, (state, action) => {
        state.deleteInProgress = false;
        state.deleteError = action.payload;
      })

      .addCase(reorderBannersThunk.rejected, (state, action) => {
        state.reorderError = action.payload;
      });
  },
});

export const { bannersReordered } = bannerAdminPageSlice.actions;
export default bannerAdminPageSlice.reducer;

// ================ Load data ================ //

export const loadData = () => dispatch => {
  // The page decides whether to render or redirect from the current user's
  // admin flag, so that has to be in the store before the banners matter.
  return dispatch(fetchCurrentUser()).then(() =>
    Promise.all([dispatch(fetchBanners()), dispatch(fetchPartnersThunk())])
  );
};
